/** Nuevo requerimiento alineado a la web: cliente + nombre + tipo + fecha. */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput, MobileTextarea } from '@/components/MobileInput';
import { SectionCard } from '@/components/SectionCard';
import { ClientAutocomplete } from '@/components/ClientAutocomplete';
import {
  useCreateRequirementMutation,
  useRequirementSequencePolicyQuery,
} from '@/hooks/useMobileRequirements';
import {
  mobileRequirementCreateSchema,
  type MobileRequirementCreateForm,
} from '@/schemas/requirement.schemas';
import { extractErrorMessage } from '@/services/mobileApiClient';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { hapticSuccess } from '@/utils/haptic';

function todayIso(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type RequirementType = 'standard' | 'project';

export function RequirementNewPage() {
  const navigate = useNavigate();
  const { user } = useMobileAuth();
  const createMutation = useCreateRequirementMutation();
  const policyQuery = useRequirementSequencePolicyQuery();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<MobileRequirementCreateForm>({
    resolver: yupResolver(mobileRequirementCreateSchema),
    defaultValues: {
      client_id: '',
      code: '',
      name: '',
      type: 'standard',
      required_date: '',
      description: '',
    },
  });

  const selectedType = (watch('type') ?? 'standard') as RequirementType;
  const manualCode = policyQuery.data?.mode === 'manual';

  function pickType(next: RequirementType) {
    setValue('type', next, { shouldValidate: true, shouldDirty: true });
  }

  async function onSubmit(values: MobileRequirementCreateForm) {
    setGeneralError(null);
    if (manualCode && (values.code ?? '').trim() === '') {
      setError('code', { message: 'El código es obligatorio' });
      return;
    }
    try {
      const created = (await createMutation.mutateAsync({
        body: {
          client_id: values.client_id,
          ...(manualCode ? { code: (values.code ?? '').trim() } : {}),
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          type: values.type,
          required_date: values.required_date,
          commercial_owner_id: user?.id,
        },
        files: [],
      })) as { id: string };
      hapticSuccess();
      toast.success('Requerimiento creado correctamente');
      navigate(`/m/requirements/${created.id}`, { replace: true });
    } catch (error) {
      setGeneralError(extractErrorMessage(error));
    }
  }

  const busy = isSubmitting || createMutation.isPending || policyQuery.isPending;

  return (
    <MobileLayout title="Nuevo requerimiento" showBack>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-4" noValidate>
        <SectionCard title="Datos del requerimiento">
          <div className="space-y-4">
            <Controller
              control={control}
              name="client_id"
              render={({ field }) => (
                <ClientAutocomplete
                  value={field.value || null}
                  onChange={(next) => field.onChange(next ?? '')}
                  label="Cliente"
                  isRequired
                  disabled={busy}
                  error={errors.client_id?.message}
                />
              )}
            />
            {manualCode && (
              <MobileInput
                label="Código"
                placeholder="Ej: RQ-2026-001"
                staticLabel
                isRequired
                autoCapitalize="characters"
                icon={<MobileIcon name="badge" className="h-5 w-5" />}
                error={errors.code?.message}
                {...register('code')}
              />
            )}
            <MobileInput
              label="Nombre"
              placeholder="Ej: Ampliacion de planta norte"
              enterKeyHint="next"
              staticLabel
              isRequired
              icon={<MobileIcon name="title" className="h-5 w-5" />}
              error={errors.name?.message}
              {...register('name')}
            />
            <div>
              <p className="mb-1.5 block truncate text-sm font-medium text-text-secondary">
                Tipo <span aria-hidden="true" className="text-error"> *</span>
              </p>
              <div
                role="radiogroup"
                aria-label="Tipo de requerimiento"
                className="inline-flex w-full gap-1 rounded-full bg-bg-tertiary p-1"
              >
                {(
                  [
                    { value: 'standard', label: 'Estandar' },
                    { value: 'project', label: 'Proyecto' },
                  ] as const
                ).map((option) => {
                  const active = selectedType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      disabled={busy}
                      onClick={() => pickType(option.value)}
                      className={clsx(
                        'flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors disabled:opacity-40',
                        active ? 'bg-white text-primary shadow-card' : 'text-text-secondary',
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {errors.type && (
                <p className="mt-1.5 break-words text-sm text-error">{errors.type.message}</p>
              )}
            </div>
            <MobileInput
              label="Fecha requerida"
              type="date"
              staticLabel
              isRequired
              min={todayIso()}
              icon={<MobileIcon name="schedule" className="h-5 w-5" />}
              error={errors.required_date?.message}
              {...register('required_date')}
            />
            <MobileTextarea
              label="Descripcion (opcional)"
              rows={3}
              placeholder="Detalle breve del pedido..."
              {...register('description')}
            />
            {errors.description && (
              <p className="-mt-2 break-words text-sm text-error">{errors.description.message}</p>
            )}
          </div>
        </SectionCard>

        <div className="flex min-h-[52px] items-center gap-2 rounded-card bg-white px-4 py-3 shadow-card">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary">
            <MobileIcon name="person" className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-text-primary">
              Responsable: {user?.full_name ?? 'Tu usuario'}
            </span>
            <span className="block truncate text-xs text-text-tertiary">
              Se asigna automaticamente al vendedor actual
            </span>
          </span>
        </div>

        {generalError && (
          <div className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
          </div>
        )}

        <MobileButton
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          icon={<MobileIcon name="save" className="h-5 w-5" />}
        >
          Guardar requerimiento
        </MobileButton>
      </form>
    </MobileLayout>
  );
}
